import { jwtDecode } from 'jwt-decode';

interface JwtPayload {
  exp?: number;
  email?: string;
  role?: string;
  [key: string]: any;
}

export class AuthManager {
  private static TOKEN_KEY = 'authToken';
  private static ROLE_KEY = 'userRole';
  private static REFRESH_THRESHOLD = 5 * 60; // 5 minutes before expiry

  static getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  static setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    
    // Extract and store role from token
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      if (decoded.role) {
        localStorage.setItem(this.ROLE_KEY, decoded.role);
      }
    } catch (error) {
      console.error('Failed to decode token:', error);
    }
  }

  static clearAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.ROLE_KEY);
  }

  static isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      if (!decoded.exp) return false; // No expiry claim, assume valid
      
      const currentTime = Date.now() / 1000;
      return decoded.exp < currentTime;
    } catch (error) {
      console.error('Failed to decode token:', error);
      return true; // If we can't decode, assume expired
    }
  }

  static shouldRefreshToken(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      if (!decoded.exp) return false;
      
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = decoded.exp - currentTime;
      
      return timeUntilExpiry < this.REFRESH_THRESHOLD;
    } catch (error) {
      return false;
    }
  }

  static getTokenExpiry(): Date | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      if (!decoded.exp) return null;
      
      return new Date(decoded.exp * 1000);
    } catch (error) {
      return null;
    }
  }

  static getUserEmail(): string | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      return decoded.email || null;
    } catch (error) {
      return null;
    }
  }

  static getUserRole(): string | null {
    return localStorage.getItem(this.ROLE_KEY);
  }

  static handleAuthError(): void {
    this.clearAuth();
    
    // Store current path for redirect after login
    sessionStorage.setItem('redirectPath', window.location.pathname);
    
    // Redirect to login
    window.location.href = '/login';
  }
}